import { useState, useCallback, useMemo, useEffect } from "react";
import {
  parsePhoneNumberFromString,
  AsYouType,
  CountryCode,
} from "libphonenumber-js";
import {
  Country,
  getCachedCountryList,
  findCountryByCode,
  detectDefaultCountry,
} from "../countryData/countries";

export type UsePhoneInputOptions = {
  /** Initial E.164 phone number value (e.g., "+15551234567") */
  value?: string;
  /** Default country code if value is empty */
  defaultCountry?: CountryCode;
  /** Callback when the E.164 value changes */
  onChange?: (e164Value: string) => void;
};

export type UsePhoneInputReturn = {
  /** List of available countries */
  countries: Country[];
  /** Currently selected country */
  selectedCountry: Country;
  /** National format phone number (without country code) */
  nationalNumber: string;
  /** Full E.164 formatted phone number */
  e164Value: string;
  /** Formatted display value (locale-aware) */
  displayValue: string;
  /** Whether the current phone number is valid */
  isValid: boolean;
  /** Change the selected country */
  setCountry: (country: Country) => void;
  /** Change the national number (user input) */
  setNationalNumber: (value: string) => void;
  /** Parse an E.164 value and update state */
  parseE164: (e164: string) => void;
};

/**
 * Hook for managing international phone number input.
 * Handles country selection, number parsing, formatting, and validation.
 *
 * @example
 * const { selectedCountry, nationalNumber, e164Value, setCountry, setNationalNumber } = usePhoneInput({
 *   value: "+15551234567",
 *   onChange: (value) => form.setValue("phoneNumber", value),
 * });
 */
export function usePhoneInput({
  value = "",
  defaultCountry,
  onChange,
}: UsePhoneInputOptions = {}): UsePhoneInputReturn {
  const countries = useMemo(() => getCachedCountryList(), []);

  // Determine initial country
  const initialCountry = useMemo(() => {
    // If we have a value, try to parse it to get the country
    if (value) {
      const parsed = parsePhoneNumberFromString(value);
      if (parsed?.country) {
        return findCountryByCode(parsed.country) ?? countries[0];
      }
    }
    // Use provided default or detect from browser
    const countryCode = defaultCountry ?? detectDefaultCountry();
    return findCountryByCode(countryCode) ?? countries[0];
  }, []);

  const [selectedCountry, setSelectedCountry] = useState<Country>(initialCountry);
  const [nationalNumber, setNationalNumberState] = useState<string>(() => {
    if (value) {
      const parsed = parsePhoneNumberFromString(value);
      if (parsed) {
        return parsed.nationalNumber;
      }
    }
    return "";
  });

  // Compute E.164 value from country and national number
  const e164Value = useMemo(() => {
    if (!nationalNumber) return "";

    const fullNumber = `+${selectedCountry.callingCode}${nationalNumber.replace(/\D/g, "")}`;
    const parsed = parsePhoneNumberFromString(fullNumber);

    if (parsed) {
      return parsed.format("E.164");
    }

    // Return the raw format if parsing fails
    return fullNumber;
  }, [selectedCountry, nationalNumber]);

  // Format for display (locale-aware national format)
  const displayValue = useMemo(() => {
    if (!nationalNumber) return "";

    // Use AsYouType for real-time formatting as user types
    const formatter = new AsYouType(selectedCountry.code);
    const formatted = formatter.input(`+${selectedCountry.callingCode}${nationalNumber}`);

    // Remove the country code prefix from the formatted output
    const prefix = `+${selectedCountry.callingCode} `;
    if (formatted.startsWith(prefix)) {
      return formatted.slice(prefix.length);
    }

    return nationalNumber;
  }, [selectedCountry, nationalNumber]);

  // Validate the phone number
  const isValid = useMemo(() => {
    if (!nationalNumber) return true; // Empty is valid (field is optional)

    const parsed = parsePhoneNumberFromString(e164Value);
    return parsed?.isValid() ?? false;
  }, [e164Value, nationalNumber]);

  // Handle country change
  const setCountry = useCallback(
    (country: Country) => {
      setSelectedCountry(country);

      // Recompute E.164 with new country
      if (nationalNumber) {
        const fullNumber = `+${country.callingCode}${nationalNumber.replace(/\D/g, "")}`;
        const parsed = parsePhoneNumberFromString(fullNumber);
        const newE164 = parsed?.format("E.164") ?? fullNumber;
        onChange?.(newE164);
      }
    },
    [nationalNumber, onChange]
  );

  // Handle national number change
  const setNationalNumber = useCallback(
    (value: string) => {
      // Strip non-digit characters for storage, but keep for display formatting
      const digitsOnly = value.replace(/\D/g, "");
      setNationalNumberState(digitsOnly);

      // Compute and emit E.164
      if (digitsOnly) {
        const fullNumber = `+${selectedCountry.callingCode}${digitsOnly}`;
        const parsed = parsePhoneNumberFromString(fullNumber);
        const newE164 = parsed?.format("E.164") ?? fullNumber;
        onChange?.(newE164);
      } else {
        onChange?.("");
      }
    },
    [selectedCountry, onChange]
  );

  // Parse an E.164 value and update state
  const parseE164 = useCallback(
    (e164: string) => {
      if (!e164) {
        setNationalNumberState("");
        return;
      }

      const parsed = parsePhoneNumberFromString(e164);
      if (parsed) {
        if (parsed.country) {
          const country = findCountryByCode(parsed.country);
          if (country) {
            setSelectedCountry(country);
          }
        }
        setNationalNumberState(parsed.nationalNumber);
      }
    },
    []
  );

  // Sync with external value changes
  useEffect(() => {
    if (value) {
      const parsed = parsePhoneNumberFromString(value);
      if (parsed) {
        if (parsed.country) {
          const country = findCountryByCode(parsed.country);
          if (country && country.code !== selectedCountry.code) {
            setSelectedCountry(country);
          }
        }
        if (parsed.nationalNumber !== nationalNumber) {
          setNationalNumberState(parsed.nationalNumber);
        }
      }
    } else if (nationalNumber && !value) {
      // External value cleared
      setNationalNumberState("");
    }
  }, [value]);

  return {
    countries,
    selectedCountry,
    nationalNumber,
    e164Value,
    displayValue,
    isValid,
    setCountry,
    setNationalNumber,
    parseE164,
  };
}

export default usePhoneInput;
